import { AppLauncher } from '@capacitor/app-launcher'
import { Capacitor } from '@capacitor/core'
import { LaunchNavigator } from '@capgo/capacitor-launch-navigator'

import { openUrl } from '@/utils/external-resources'

import type { Address, Coordinates } from '@/modules/shared/types/location.types'

// Opens the platform's maps app on the address. Returns whether a link was opened, so the caller
// can track the tap; tracking is the caller's job because each surface reports its own event.
function navigateMaps(address: Address | string | undefined): boolean {
  let addressString: string | undefined

  if (typeof address === 'string') {
    addressString = address
  } else if (address) {
    addressString = encodeURIComponent(`${address?.street} ${address?.city} ${address?.postcode}`)
  }

  if (!addressString) {
    return false
  }

  // Create the maps url based on the device platform
  const platform = Capacitor.getPlatform() === 'android' ? 'google' : 'apple'
  const url = `http://maps.${platform}.com?daddr=${addressString ?? 'null'}`
  openUrl(url)
  return true
}

// An address can be navigated to when there is something to geocode: a free-text address, or at
// least one of street, postcode and city. Shared with the call-sites so a card is only tappable
// when a tap will open something.
function hasNavigableAddress(address: Address | string | undefined): boolean {
  if (typeof address === 'string') {
    return address.trim().length > 0
  }

  return Boolean(address?.street || address?.postcode || address?.city)
}

type NavigableLocation = {
  address?: Address | string
  coordinates?: Coordinates
}

// The app the user set as default for navigation is reached through a URL the OS resolves, not
// through the plugin, which always launches one fixed app. iOS 18.4+ routes `geo-navigation://` to
// the app chosen under Settings (EU only; elsewhere the scheme cannot be opened). Android resolves
// a plain `geo:` link through the handler the user made default, or its own chooser. Returns whether
// the OS opened something, so the caller can fall through to the plugin.
async function openDefaultNavigationApp(coordinates: Coordinates): Promise<boolean> {
  const destination = `${coordinates.latitude},${coordinates.longitude}`
  const platform = Capacitor.getPlatform()
  const url =
    platform === 'ios'
      ? `geo-navigation://directions?destination=${destination}`
      : platform === 'android'
        ? `geo:${destination}?q=${destination}`
        : undefined

  if (!url) {
    return false
  }

  try {
    const { value: canOpen } = await AppLauncher.canOpenUrl({ url })
    if (!canOpen) {
      return false
    }
    const { completed } = await AppLauncher.openUrl({ url })
    return completed
  } catch (error) {
    console.error('Error opening the default navigation app:', error)
    return false
  }
}

// Coordinates first: they point at the campus itself, where an address may geocode to a different
// entrance. The user's default navigation app gets the first go, then the plugin's fixed app. When
// neither can be opened, the address link is the fallback, so a tap never ends in an error. The
// result says which of the two was used.
async function navigateToLocation(location: NavigableLocation): Promise<'coordinates' | 'address' | undefined> {
  const { address, coordinates } = location

  if (coordinates) {
    if (await openDefaultNavigationApp(coordinates)) {
      return 'coordinates'
    }

    try {
      await LaunchNavigator.navigate({ destination: [coordinates.latitude, coordinates.longitude] })
      return 'coordinates'
    } catch (error) {
      console.error('Error opening navigation app:', error)
    }
  }

  if (hasNavigableAddress(address)) {
    navigateMaps(address)
    return 'address'
  }

  return undefined
}

export { hasNavigableAddress, navigateMaps, navigateToLocation }
